import { AHIcon, AHText, Box, ListItem, SectionHeader } from "arkh";
import { FormattedMessage, useIntl } from "react-intl";
import React, { Suspense, useCallback } from "react";

import {
  graphql,
  useFragment,
  useLazyLoadQuery,
  useMutation,
} from "react-relay";
import { StorecastScreenAdminQuery } from "./__generated__/StorecastScreenAdminQuery.graphql";
import { StorecastScreenAdminDeregisterDeviceMutation } from "./__generated__/StorecastScreenAdminDeregisterDeviceMutation.graphql";
import { LazyQueryNetworkBoundrary } from "../../components/mobile/QueryNetworkBoundrary";
import { StorecastScreenAdminSchedule_StorecastScreen$key } from "./__generated__/StorecastScreenAdminSchedule_StorecastScreen.graphql";
import { useWeekdays } from "../../common/useWeekdays";
import { useAlert } from "../../common/Alert";
import {
  StoreAdminNavigationProp,
  StoreAdminNavigationScreenProps,
} from "../StoreAdmin/StoreAdminNavigation";
import { InfoBox } from "./AddScreenInfo/InfoBox";
import { StorecastScreenAdminDismissInfoMutation } from "./__generated__/StorecastScreenAdminDismissInfoMutation.graphql";
import { useOnBeforeLeaveScreen } from "../Storecast/Storecast/useOnBeforeLeaveScreen";
import { StorecastScreenAdminContent_StorecastScreen$key } from "./__generated__/StorecastScreenAdminContent_StorecastScreen.graphql";
import { useIntlTokens } from "../../components/mobile/useIntlTokens";
import { KeyboardAwareScrollView } from "react-native-keyboard-controller";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { EntityHeader } from "@/src/components/EntityHeader";
import { useNavigationHeader } from "@/src/common/navigationHeaderOptions";

import { StorecastScreenModeSelector } from "./StorecastScreenAdminModeSelector";

type StorecastDeviceAdminProps = StoreAdminNavigationScreenProps<"ScreenAdmin">;

const StorecastScreenAdminComponent = ({
  route: { params },
  fetchKey,
  navigation,
}: StorecastDeviceAdminProps & { fetchKey: string }) => {
  const intl = useIntl();

  const [deregisterMutation, derigesterInFlight] =
    useMutation<StorecastScreenAdminDeregisterDeviceMutation>(graphql`
      mutation StorecastScreenAdminDeregisterDeviceMutation($id: ID!) {
        storecast {
          deregisterScreen(screenId: $id) {
            id
            status
          }
        }
      }
    `);

  const deregister = () => {
    if (derigesterInFlight) {
      return;
    }
    deregisterMutation({
      variables: {
        id: params.screen.id,
      },
      onCompleted: () => {
        navigation.goBack();
      },
    });
  };

  const Alert = useAlert();
  useNavigationHeader({
    type: "DONE",
    defaultContentStyle: true,
    onDone: () => {
      navigation.goBack();
    },
    more: [
      {
        icon: "trash",
        color: "negative",
        type: "button",
        label: intl.formatMessage({
          defaultMessage: "Delete Screen",
          description: "delete screen btn title",
        }),
        onPress: () => {
          Alert.alert(
            intl.formatMessage({
              defaultMessage: "Delete Screen",
              description: "delete screen confirmation alert title",
            }),
            intl.formatMessage({
              defaultMessage: "Are you sure you want to delete this screen?",
              description: "delete screen confirmation alert description",
            }),
            [
              {
                text: intl.formatMessage({
                  defaultMessage: "Cancel",
                  description: "delete screen confirmation alert cancel option",
                }),
                style: "cancel",
              },
              {
                text: intl.formatMessage({
                  defaultMessage: "Delete",
                  description: "delete screen confirmation alert delete option",
                }),
                style: "destructive",
                onPress: () => {
                  deregister();
                },
              },
            ]
          );
        },
      },
    ],
  });

  const data = useLazyLoadQuery<StorecastScreenAdminQuery>(
    graphql`
      query StorecastScreenAdminQuery($screenId: ID!) {
        storecast {
          screen(id: $screenId) {
            id
            name
            showInfo
            ...StorecastScreenAdminContent_StorecastScreen
          }
        }
      }
    `,
    { screenId: params.screen.id },
    {
      fetchPolicy: "store-and-network",
      fetchKey,
    }
  );

  const [dismissInfo] = useMutation<StorecastScreenAdminDismissInfoMutation>(
    graphql`
      mutation StorecastScreenAdminDismissInfoMutation($screenId: ID!) {
        storecast {
          dismissInfo(screenId: $screenId) {
            id
          }
        }
      }
    `
  );

  const dismiss = useCallback(() => {
    if (data.storecast.screen.showInfo) {
      dismissInfo({ variables: { screenId: data.storecast.screen.id } });
    }
  }, [data.storecast.screen.id, data.storecast.screen.showInfo, dismissInfo]);

  useOnBeforeLeaveScreen(dismiss);

  const insets = useSafeAreaInsets();
  return (
    <KeyboardAwareScrollView
      contentContainerStyle={{
        justifyContent: "flex-start",
        paddingBottom: insets.bottom,
      }}
      style={{
        flex: 1,
      }}
    >
      <EntityHeader>
        <EntityHeader.Icon icon="tv" />
        <EntityHeader.Title>{data.storecast.screen.name}</EntityHeader.Title>

        <EntityHeader.Button
          onPress={() => {
            navigation.navigate("ScreenAdminEditName", {
              screenId: data.storecast.screen.id,
            });
          }}
        >
          <FormattedMessage
            defaultMessage="Edit Name"
            description="StorecastScreenAdmin edit name and icon button title"
          />
        </EntityHeader.Button>
      </EntityHeader>
      <Box space="medium">
        <Suspense fallback={null}>
          <ScreenAdminContent
            navigation={navigation}
            screen={data.storecast.screen}
          />
        </Suspense>
      </Box>
    </KeyboardAwareScrollView>
  );
};

function ScreenAdminContent(props: {
  navigation: StoreAdminNavigationProp<"ScreenAdmin">;
  screen: StorecastScreenAdminContent_StorecastScreen$key;
}) {
  const screen = useFragment(
    graphql`
      fragment StorecastScreenAdminContent_StorecastScreen on StorecastScreen {
        id
        name
        defaultDashboard {
          id
          name
        }
        ...StorecastScreenAdminModeSelector_StorecastScreen
        ...StorecastScreenAdminSchedule_StorecastScreen
        ...InfoBox_StorecastScreen
      }
    `,
    props.screen
  );

  const intl_tokens = useIntlTokens();

  return (
    <>
      <SectionHeader>
        <SectionHeader.Title>
          <FormattedMessage
            description="StorecastScreenAdmin storecast section title"
            defaultMessage="Storecast"
          />
        </SectionHeader.Title>
      </SectionHeader>
      <ListItem
        isFirst
        isLast
        border="border-primary"
        right={
          <AHText color="primary" type="subtitle">
            {intl_tokens.Change}
          </AHText>
        }
        onPress={() =>
          props.navigation.navigateDeprecated("ScreenAdminSelectDashboard", {
            screenId: screen.id,
            currentDashboardId: screen.defaultDashboard?.id,
          })
        }
      >
        <ListItem.Title>{screen.defaultDashboard?.name}</ListItem.Title>
      </ListItem>
      <Box spaceBottom="medium" />

      <SectionHeader>
        <SectionHeader.Title>
          <FormattedMessage
            description="StorecastScreenAdmin info section title"
            defaultMessage="Screen status"
          />
        </SectionHeader.Title>
      </SectionHeader>

      <InfoBox screen={screen} />
      <Box spaceBottom="large" />

      <SectionHeader>
        <SectionHeader.Title>
          <FormattedMessage
            description="Storecast Admin Scheduled storecast title"
            defaultMessage="Scheduled Storecasts"
          />
        </SectionHeader.Title>
      </SectionHeader>
      <StorecastScreenSchedule
        screen={screen}
        onAdd={() =>
          props.navigation.navigateDeprecated("AddOrEditScheduleItem", {
            screen: {
              id: screen.id,
              name: screen.name,
            },
          })
        }
        onEdit={(scheduleId: string) =>
          props.navigation.navigateDeprecated("AddOrEditScheduleItem", {
            screen: {
              id: screen.id,
              name: screen.name,
            },
            scheduleId,
          })
        }
      />

      <StorecastScreenModeSelector screen={screen} />
    </>
  );
}

function StorecastScreenSchedule(props: {
  screen: StorecastScreenAdminSchedule_StorecastScreen$key;
  onAdd: () => void;
  onEdit: (scheduleId: string) => void;
}) {
  const screen = useFragment(
    graphql`
      fragment StorecastScreenAdminSchedule_StorecastScreen on StorecastScreen {
        id
        scheduledStorecasts {
          id
          storecast {
            id
            name
          }
          startAt
          endAt
          weekdays
        }
      }
    `,
    props.screen
  );

  const weekdays = useWeekdays("long");

  return (
    <>
      {screen.scheduledStorecasts.map((scheduledStorecast, index) => (
        <ListItem
          border="border-primary"
          isFirst={index === 0}
          key={scheduledStorecast.id}
          onPress={() => {
            props.onEdit(scheduledStorecast.id);
          }}
          right={
            <Box>
              <AHText style={{ textAlign: "right" }} type="subtitle">
                {scheduledStorecast.startAt} - {scheduledStorecast.endAt}
              </AHText>
              <AHText style={{ textAlign: "right" }} type="detail">
                {scheduledStorecast.weekdays.length === 0 ||
                scheduledStorecast.weekdays.length === 7
                  ? ""
                  : scheduledStorecast.weekdays
                      .map((w) => dayToDayIndex(w))
                      .sort()
                      .map((w) => weekdays[w])
                      .join(", ")}
              </AHText>
            </Box>
          }
        >
          <ListItem.Title>{scheduledStorecast.storecast?.name}</ListItem.Title>
        </ListItem>
      ))}
      <ListItem
        border="border-primary"
        isFirst={screen.scheduledStorecasts.length === 0}
        isLast
        right={
          <AHText color="primary" type="subtitle">
            <AHIcon type="add-circle" color="primary" />
          </AHText>
        }
        onPress={props.onAdd}
      >
        <AHText type="title-small" color="text-cta">
          <FormattedMessage
            description="Storecast Admin schedule add dashboard"
            defaultMessage="Add dashboard"
          />
        </AHText>
      </ListItem>
    </>
  );
}

export const StorecastScreenAdmin = (props: StorecastDeviceAdminProps) => {
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(fetchKey) => (
        <StorecastScreenAdminComponent
          fetchKey={fetchKey}
          navigation={props.navigation}
          route={props.route}
        />
      )}
    </LazyQueryNetworkBoundrary>
  );
};

function dayToDayIndex(
  day:
    | "MONDAY"
    | "TUESDAY"
    | "WEDNESDAY"
    | "THURSDAY"
    | "FRIDAY"
    | "SATURDAY"
    | "SUNDAY"
) {
  switch (day) {
    case "MONDAY":
      return 0;
    case "TUESDAY":
      return 1;
    case "WEDNESDAY":
      return 2;
    case "THURSDAY":
      return 3;
    case "FRIDAY":
      return 4;
    case "SATURDAY":
      return 5;
    case "SUNDAY":
      return 6;
  }
}
